"use client"
import styled from "@emotion/styled"
import { LayoutContext } from "contexts/LayoutProvider"
import Image from "next/image"
import { useContext, useEffect, useState } from "react"
import { getUmbracoMediaUrl, replaceHrefsWithMediaUrl } from "utils/utils"

const COOKIE_CONSENT_KEY = "paz_cookie_consent"

const StyledBanner = styled.div<{ isVisible: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: #ffffff;
  border-top: 4px solid #ffd600;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
  padding: 28px 28px 16px;;
  display: ${({ isVisible }) => (isVisible ? "flex" : "none")};
  flex-direction: column;
  gap: 16px;
  transition: transform 0.3s ease-in-out;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 20px 40px;
    gap: 10px;
    flex-direction: row;
    align-items: center;
  }
`

const StyledContentWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex: 1;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 10px;
    align-items: center;
  }
`

const StyledIconWrapper = styled.div`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  position: relative;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 14px;
    height: 14px;
  }
`

const StyledContent = styled.div`
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: #0b253c;

  p {
    margin: 0;
  }

  a {
    color: #0050c9;
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 0.2s ease;

    &:hover {
      color: #003d99;
    }

    &:focus-visible {
      outline: 2px solid #153e69;
      outline-offset: 2px;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
    font-weight: 500;
  }
`

const StyledCloseButton = styled.button`
  flex-shrink: 0;
  background: #0050c9;
  color: #ffffff;
  border: none;
  border-radius: 24px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, outline 0.2s ease;
  align-self: flex-end;
  &:hover {
    background: #003d99;
  }

  &:focus-visible {
    outline: 2px solid #153e69;
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
    padding: 12px 32px;
    align-self: initial;
  }
`

type CookieBannerProps = {
  data: any
}

const CookieBanner = ({ data }: CookieBannerProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const { GRAPHQL_URL } = useContext(LayoutContext)

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!hasConsented) {
      setIsVisible(true)
    }
  }, [])

  const handleClose = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true")
    setIsVisible(false)
  }

  if (!data?.cookieConsent) {
    return null
  }

  const cookieData = data.cookieConsent

  // Extract properties
  const iconUrl =
    cookieData.properties?.find((p: any) => p.propertyName === "icon")
      ?.propertyValue?.mediaItems?.[0]?.url || "/cookie-bite.svg"

  const textHtml =
    cookieData.properties?.find((p: any) => p.propertyName === "text")
      ?.propertyValue?.richText || ""

  // Remove leading <br> tags inside <p> tags
  const cleanedTextHtml = textHtml.replace(/<p>\s*<br\s*\/?>\s*/gi, '<p>')

  const buttonText =
    cookieData.properties?.find((p: any) => p.propertyName === "buttonText")
      ?.propertyValue?.value || "סגירה"

  return (
    <StyledBanner isVisible={isVisible} role="dialog" aria-label="הודעת עוגיות">
      <StyledContentWrapper>
        <StyledIconWrapper>
          <Image
            src={getUmbracoMediaUrl(iconUrl)}
            alt=""
            fill
            style={{ objectFit: "contain" }}
          />
        </StyledIconWrapper>
        <StyledContent
          dangerouslySetInnerHTML={{
            __html: replaceHrefsWithMediaUrl(cleanedTextHtml, GRAPHQL_URL),
          }}
        />
      </StyledContentWrapper>
      <StyledCloseButton
        onClick={handleClose}
        aria-label={`${buttonText} - סגירת הודעת עוגיות`}
      >
        {buttonText}
      </StyledCloseButton>
    </StyledBanner>
  )
}

export default CookieBanner
